import React, { useState, useEffect, useMemo } from 'react';
import '../pages/Policies.css';
import { Icon, Drawer, ContextMenu, CodeToken, Modal, Select, SelectItem } from '../components/ui';
import CelEditor, { CelVocab } from '../components/CelEditor';
import PolicyBindings from '../components/PolicyBindings';
import PolicyStudio from '../components/PolicyStudio';
import { usePermission } from '../hooks/usePermission';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

type PolicyView = 'list' | 'bindings';

interface PolicyEditorProps {
  apiUrl: string;
}

interface PolicyConditions {
  roles: string[];
  tools: string[];
  time_window?: { start: string; end: string };
  ip_restrictions?: string[];
  expr?: string;
}

interface ExpressionMeta {
  variables: { name: string; type: string; detail: string }[];
  functions: { name: string; signature: string; detail: string }[];
  roles: string[];
}

interface ExprCheck {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

interface Policy {
  policy_id: string;
  name: string;
  description?: string;
  org_id: string;
  effect: 'allow' | 'deny';
  conditions: PolicyConditions;
  blocks?: Record<string, { enabled?: boolean } | undefined>;
  tags?: string[];
  created_at: string;
  created_by?: string;
  status: 'active' | 'draft' | 'archived';
}

// Compact labels for the rule-block badges shown on each policy row.
const getBLOCK_BADGES = (t: (key: string) => string): { key: string; label: string }[] => [
  { key: 'access', label: t('pages.policies.access') },
  { key: 'data_protection', label: 'DLP' },
  { key: 'threat', label: t('pages.policies.injection') },
  { key: 'budget', label: t('pages.policies.budget') },
  { key: 'routing', label: t('pages.policies.routing') },
];

interface MCPService {
  name: string;
  tools?: { name: string; description?: string }[];
}

interface CommandParseResult {
  effect: 'allow' | 'deny';
  roles: string[];
  tools: string[];
  name: string;
  warnings: string[];
  unresolved: string[];
  source: 'llm' | 'deterministic';
}

const COMMAND_EXAMPLES = [
  'Allow developers and viewers access to database-tools and ad-tools',
  'Deny viewers access to database-tools.query_database',
  'Allow admins to use everything',
];

const POLICY_STATUS_KEY: Record<string, string> = {
  active: 'pages.policies.statusActive',
  draft: 'pages.policies.statusDraft',
  archived: 'pages.policies.statusArchived',
};

const emptyForm = (): { name: string; description: string; effect: 'allow' | 'deny'; conditions: PolicyConditions; tags: string[] } => ({
  name: '',
  description: '',
  effect: 'allow',
  conditions: { roles: ['developer'], tools: [] },
  tags: [],
});

const Policies: React.FC<PolicyEditorProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const { t: tr } = useTranslation();
  // RBAC: only Admin/SecurityOfficer can create/edit; only Admin can delete.
  const canCreatePolicy = usePermission('create:policy');
  const canDeletePolicy = usePermission('delete:policy');
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [mcpServices, setMcpServices] = useState<MCPService[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedServer, setSelectedServer] = useState<string>('');
  const [availableTools, setAvailableTools] = useState<string[]>([]);
  const [formData, setFormData] = useState(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  // Advanced expression mode
  const [mode, setMode] = useState<'form' | 'expr' | 'command'>('form');
  const [expr, setExpr] = useState('');
  const [meta, setMeta] = useState<ExpressionMeta>({ variables: [], functions: [], roles: [] });
  const [exprCheck, setExprCheck] = useState<ExprCheck | null>(null);
  const [validating, setValidating] = useState(false);
  // Command (natural-language) mode
  const [commandText, setCommandText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState<CommandParseResult | null>(null);
  const [parserEngine, setParserEngine] = useState<{ engine: string; model: string } | null>(null);
  // View switcher + organization
  const [view, setView] = useState<PolicyView>('list');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [groupByTag, setGroupByTag] = useState(false);
  const [tagDraft, setTagDraft] = useState('');
  // Unified Policy Studio editor (one screen, all rule blocks).
  const [showStudio, setShowStudio] = useState(false);
  const [studioEditing, setStudioEditing] = useState<Policy | null>(null);
  const openStudioNew = () => { setStudioEditing(null); setShowStudio(true); };
  const openStudioEdit = (policy: Policy) => { setStudioEditing(policy); setShowStudio(true); };

  useEffect(() => {
    fetchPolicies();
    fetchMcpServices();
    fetchExpressionMeta();
    fetchParserStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  // Vocabulary for the expression editor: variables/functions from the server,
  // tool names derived from the live MCP service catalog.
  const vocab: CelVocab = useMemo(() => {
    const tools: string[] = [];
    mcpServices.forEach((s) => {
      tools.push(`${s.name}.*`);
      (s.tools || []).forEach((t) => tools.push(`${s.name}.${t.name}`));
    });
    return {
      variables: meta.variables,
      functions: meta.functions,
      roles: meta.roles.length ? meta.roles : ['viewer', 'developer', 'admin'],
      tools,
    };
  }, [mcpServices, meta]);

  // Debounced, server-authoritative validation of the expression.
  useEffect(() => {
    if (mode !== 'expr' || !expr.trim()) {
      setExprCheck(null);
      setValidating(false);
      return;
    }
    setValidating(true);
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`${apiUrl}/admin/policies/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ expr }),
        });
        const data = await res.json();
        setExprCheck({ valid: !!data.valid, errors: data.errors || [], warnings: data.warnings || [] });
      } catch {
        setExprCheck({ valid: false, errors: ['Could not reach validator'], warnings: [] });
      } finally {
        setValidating(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [expr, mode, apiUrl]);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${apiUrl}/admin/policies`);
      if (response.ok) {
        const data = await response.json();
        setPolicies(data.policies || []);
      } else {
        setError('Failed to fetch policies');
      }
    } catch (error) {
      console.error('Failed to fetch policies:', error);
      setError('Error loading policies. Make sure the firewall is running.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMcpServices = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/mcp/available-services`);
      if (response.ok) {
        const data = await response.json();
        const services = (data.services || []).map((service: any) => ({
          name: service.name,
          tools: service.tools
            ? service.tools.map((t: any) => ({
                name: typeof t === 'string' ? t : t.name,
                description: typeof t === 'string' ? undefined : t.description,
              }))
            : [],
        }));
        setMcpServices(services);
      }
    } catch (error) {
      console.error('Failed to fetch MCP services:', error);
    }
  };

  const fetchExpressionMeta = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/policies/expression-meta`);
      if (response.ok) {
        const data = await response.json();
        setMeta({
          variables: data.variables || [],
          functions: data.functions || [],
          roles: data.roles || [],
        });
      }
    } catch (error) {
      console.error('Failed to fetch expression metadata:', error);
    }
  };

  const fetchParserStatus = async () => {
    try {
      const response = await fetch(`${apiUrl}/admin/policies/parser-status`);
      if (response.ok) {
        const data = await response.json();
        setParserEngine({ engine: data.engine, model: data.model });
      }
    } catch (error) {
      console.error('Failed to fetch parser status:', error);
    }
  };

  const runParseCommand = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setParsing(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/admin/policies/parse-command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: trimmed }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.message || 'Failed to parse command');
        return;
      }
      const result: CommandParseResult = await response.json();
      setParseResult(result);
      // Prefill the shared name + effect fields so the existing create flow applies.
      setFormData((f) => ({ ...f, name: result.name, effect: result.effect }));
    } catch (error) {
      console.error('Failed to parse command:', error);
      setError('Error parsing command. Make sure the firewall is running.');
    } finally {
      setParsing(false);
    }
  };

  // Form/expression/command drawer. Policy Studio (openStudioNew) is the primary
  // block editor; this drawer additionally exposes the natural-language "Command"
  // mode (LLM-parsed), reachable via the "Describe in words" button when the
  // Ollama-backed parser is healthy (parserEngine.engine === 'llm').
  const _legacyOpenForm = () => {
    setEditingPolicy(null);
    setFormData(emptyForm());
    setSelectedServer('');
    setAvailableTools([]);
    setError(null);
    setMode('form');
    setExpr('');
    setExprCheck(null);
    setCommandText('');
    setParseResult(null);
    setTagDraft('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (!formData.name || !formData.effect) {
        setError('Name and effect are required');
        return;
      }
      let conditions: PolicyConditions;
      if (mode === 'expr') {
        if (!expr.trim()) {
          setError('Expression is required');
          return;
        }
        if (exprCheck && !exprCheck.valid) {
          setError('Fix the expression errors before saving');
          return;
        }
        // Structured fields default to wildcard; the expression carries the real logic.
        conditions = { roles: ['*'], tools: ['*'], expr: expr.trim() };
      } else if (mode === 'command') {
        if (!parseResult || !parseResult.roles.length || !parseResult.tools.length) {
          setError('Parse a command into at least one role and one tool first');
          return;
        }
        conditions = { roles: parseResult.roles, tools: parseResult.tools };
      } else {
        if (!formData.conditions.roles.length || !formData.conditions.tools.length) {
          setError('At least one role and one tool must be specified');
          return;
        }
        conditions = formData.conditions;
      }
      const method = editingPolicy ? 'PUT' : 'POST';
      const url = editingPolicy ? `${apiUrl}/admin/policies/${editingPolicy.policy_id}` : `${apiUrl}/admin/policies`;
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || '',
          effect: formData.effect,
          conditions,
          tags: formData.tags,
        }),
      });
      if (response.ok) {
        setShowForm(false);
        setEditingPolicy(null);
        setFormData(emptyForm());
        fetchPolicies();
      } else {
        const data = await response.json();
        setError(data.message || (editingPolicy ? 'Failed to update policy' : 'Failed to create policy'));
      }
    } catch (error) {
      console.error('Failed to submit policy:', error);
      setError(editingPolicy ? 'Error updating policy' : 'Error creating policy');
    }
  };

  const handleDelete = async (policyId: string) => {
    setShowDeleteConfirm(policyId);
  };

  const confirmDelete = async () => {
    if (!showDeleteConfirm) return;
    try {
      setDeleting(showDeleteConfirm);
      setError(null);
      const response = await fetch(`${apiUrl}/admin/policies/${showDeleteConfirm}`, { method: 'DELETE' });
      if (response.ok) {
        setShowDeleteConfirm(null);
        fetchPolicies();
      } else {
        setError('Failed to delete policy');
      }
    } catch (error) {
      console.error('Failed to delete policy:', error);
      setError('Error deleting policy');
    } finally {
      setDeleting(null);
    }
  };

  const setConditions = (patch: Partial<PolicyConditions>) =>
    setFormData((f) => ({ ...f, conditions: { ...f.conditions, ...patch } }));

  const addRole = () => setConditions({ roles: [...formData.conditions.roles, 'viewer'] });
  const removeRole = (index: number) =>
    setConditions({ roles: formData.conditions.roles.filter((_, i) => i !== index) });

  const handleServerSelect = (serverName: string) => {
    setSelectedServer(serverName);
    const server = mcpServices.find((s) => s.name === serverName);
    setAvailableTools(server?.tools ? ['*', ...server.tools.map((t) => t.name)] : ['*']);
  };

  const removeTool = (index: number) =>
    setConditions({ tools: formData.conditions.tools.filter((_, i) => i !== index) });

  // --- Tags (drawer) --------------------------------------------------------
  const addTag = (raw: string) => {
    const t = raw.trim().slice(0, 40);
    if (!t || formData.tags.includes(t)) return;
    setFormData((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagDraft('');
  };
  const removeTag = (tag: string) =>
    setFormData((f) => ({ ...f, tags: f.tags.filter((x) => x !== tag) }));

  // --- Derived data for filtering --------------------------------------------
  const allTags = useMemo(() => {
    const set = new Set<string>();
    policies.forEach((p) => (p.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [policies]);

  const visiblePolicies = useMemo(
    () => (tagFilter ? policies.filter((p) => (p.tags || []).includes(tagFilter)) : policies),
    [policies, tagFilter]
  );

  // Group visible policies by tag for the List view (an "Untagged" bucket last).
  const groupedPolicies = useMemo(() => {
    const groups: { tag: string; items: Policy[] }[] = [];
    const byTag = new Map<string, Policy[]>();
    visiblePolicies.forEach((p) => {
      const tags = p.tags && p.tags.length ? p.tags : ['__untagged__'];
      tags.forEach((t) => {
        if (!byTag.has(t)) byTag.set(t, []);
        byTag.get(t)!.push(p);
      });
    });
    Array.from(byTag.keys())
      .sort((a, b) => (a === '__untagged__' ? 1 : b === '__untagged__' ? -1 : a.localeCompare(b)))
      .forEach((t) => groups.push({ tag: t === '__untagged__' ? 'Untagged' : t, items: byTag.get(t)! }));
    return groups;
  }, [visiblePolicies]);

  const renderPolicyRow = (policy: Policy) => (
    <tr key={policy.policy_id} className={`policy-${policy.effect}`}>
      <td style={{ fontWeight: 500 }}>
        {policy.name}
        {policy.blocks && (
          <span className="row-tags" style={{ display: 'inline-flex', gap: 4, marginLeft: 8 }}>
            {getBLOCK_BADGES(t).filter((b) => policy.blocks?.[b.key]?.enabled).map((b) => (
              <span key={b.key} className="tag-chip" style={{ background: 'var(--color-bg-subtle, #eef)', fontSize: 11 }}>{b.label}</span>
            ))}
          </span>
        )}
        {(policy.tags || []).length > 0 && (
          <span className="row-tags">
            {(policy.tags || []).map((t) => (
              <span key={t} className="tag-chip">{t}</span>
            ))}
          </span>
        )}
      </td>
      <td>
        <span className={`badge badge-${policy.effect}`}>{tr(policy.effect === 'allow' ? 'pages.policies.effectAllow' : 'pages.policies.effectDeny')}</span>
      </td>
      {policy.conditions.expr ? (
        <td colSpan={2}>
          <span className="expr-tag">{tr('pages.policies.expr')}</span>
          <code className="expr-cell">{policy.conditions.expr}</code>
        </td>
      ) : (
        <>
          <td>
            <span className="token-row">
              {(policy.conditions.roles || []).map((r, i) => (
                <CodeToken key={i} muted>
                  {r}
                </CodeToken>
              ))}
              {!(policy.conditions.roles || []).length && <span style={{ color: 'var(--text-3)' }}>—</span>}
            </span>
          </td>
          <td>
            <span className="token-row">
              {(policy.conditions.tools || []).map((t, i) => (
                <CodeToken key={i}>{t}</CodeToken>
              ))}
              {!(policy.conditions.tools || []).length && <span style={{ color: 'var(--text-3)' }}>—</span>}
            </span>
          </td>
        </>
      )}
      <td style={{ color: 'var(--text-2)', textTransform: 'capitalize' }}>{tr(POLICY_STATUS_KEY[policy.status] || policy.status)}</td>
      <td style={{ color: 'var(--text-2)' }}>{parseServerDate(policy.created_at).toLocaleDateString()}</td>
      <td style={{ textAlign: 'right' }}>
        <ContextMenu
          items={[
            ...(canCreatePolicy
              ? [{ label: t('pages.policies.editPolicy'), icon: 'info' as const, onClick: () => openStudioEdit(policy) }]
              : [{ label: t('pages.policies.viewPolicy'), icon: 'view' as const, onClick: () => openStudioEdit(policy) }]),
            ...(canDeletePolicy
              ? [{ label: t('pages.policies.deletePolicy'), icon: 'trash' as const, danger: true, onClick: () => handleDelete(policy.policy_id) }]
              : []),
          ]}
        />
      </td>
    </tr>
  );

  const renderTable = (items: Policy[]) => (
    <div className="ent-table-wrap scroll">
      <table className="ent-table">
        <thead>
          <tr>
            <th>{tr('pages.policies.name')}</th>
            <th style={{ width: 90 }}>{tr('pages.policies.effect')}</th>
            <th>{tr('pages.policies.roles')}</th>
            <th>{tr('pages.policies.tools')}</th>
            <th style={{ width: 90 }}>{tr('pages.policies.status')}</th>
            <th style={{ width: 110 }}>{tr('pages.policies.created')}</th>
            <th style={{ width: 60, textAlign: 'right' }}>{tr('pages.policies.actions')}</th>
          </tr>
        </thead>
        <tbody>{items.map(renderPolicyRow)}</tbody>
      </table>
    </div>
  );

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="shield" size={20} /> {tr('pages.policies.policyConfiguration')}
          </h1>
          <p className="subtitle">{tr('pages.policies.defineWhoCanAccessWhichTools')}</p>
        </div>
        {canCreatePolicy && (
          <div style={{ display: 'flex', gap: 8 }}>
            {parserEngine?.engine === 'llm' && (
              <button className="ent-btn" onClick={_legacyOpenForm} title={tr('pages.policies.describePolicyTitle', { model: parserEngine.model || 'local LLM' })}>
                <Icon name="bolt" />
                {tr('pages.policies.describeInWords')}
              </button>
            )}
            <button className="ent-btn primary" onClick={openStudioNew}>
              <Icon name="plus" />
              {tr('pages.policies.newPolicy')}
            </button>
          </div>
        )}
      </div>

      <div className="policy-toolbar">
        <div className="view-switch" role="group" aria-label={tr('pages.policies.policyView')}>
          <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>
            <Icon name="logs" /> {tr('pages.policies.list')}
          </button>
          <button className={view === 'bindings' ? 'active' : ''} onClick={() => setView('bindings')}>
            <Icon name="key" /> {tr('pages.policies.bindings')}
          </button>
        </div>

        {(allTags.length > 0 || view === 'list') && (
          <div className="tag-filter">
            {allTags.length > 0 && (
              <>
                <button
                  className={`tag-pill ${tagFilter === null ? 'active' : ''}`}
                  onClick={() => setTagFilter(null)}
                >
                  {tr('pages.policies.all')}
                </button>
                {allTags.map((t) => (
                  <button
                    key={t}
                    className={`tag-pill ${tagFilter === t ? 'active' : ''}`}
                    onClick={() => setTagFilter((cur) => (cur === t ? null : t))}
                  >
                    <Icon name="key" /> {t}
                  </button>
                ))}
              </>
            )}
            {view === 'list' && (
              <label className="group-toggle">
                <input type="checkbox" checked={groupByTag} onChange={(e) => setGroupByTag(e.target.checked)} />
                {tr('pages.policies.groupByTag')}
              </label>
            )}
          </div>
        )}
      </div>

      {error && !showForm && (
        <div className="notice error">
          <Icon name="warning" />
          {error}
        </div>
      )}

      {loading ? (
        <p className="muted-text">{tr('pages.policies.loadingPolicies')}</p>
      ) : view === 'bindings' ? (
        <PolicyBindings apiUrl={apiUrl} policies={policies} />
      ) : visiblePolicies.length > 0 ? (
        groupByTag ? (
          <div className="policy-groups">
            {groupedPolicies.map((g) => (
              <section key={g.tag} className="policy-group">
                <h4 className="group-head">
                  <Icon name="key" /> {g.tag} <span className="group-count">{g.items.length}</span>
                </h4>
                {renderTable(g.items)}
              </section>
            ))}
          </div>
        ) : (
          renderTable(visiblePolicies)
        )
      ) : (
        <div className="ent-empty">
          <span className="icon">
            <Icon name="shield" />
          </span>
          <h4>{tagFilter ? `No policies tagged "${tagFilter}"` : tr('pages.policies.noPoliciesDefined')}</h4>
          <p>
            {tagFilter
              ? tr('pages.policies.tryADifferentTagOrClear')
              : tr('pages.policies.createAPolicyToGrantScoped')}
          </p>
        </div>
      )}

      {/* Create/Edit Policy — slide-out drawer */}
      {showStudio && (
        <PolicyStudio
          apiUrl={apiUrl}
          editing={studioEditing as any}
          mcpServices={mcpServices}
          onClose={() => setShowStudio(false)}
          onSaved={() => { setShowStudio(false); fetchPolicies(); }}
        />
      )}

      {showForm && (
        <Drawer
          title={editingPolicy ? tr('pages.policies.editPolicy') : tr('pages.policies.createPolicy')}
          width={520}
          onClose={() => { setShowForm(false); setEditingPolicy(null); }}
          footer={
            <>
              <button className="ent-btn" onClick={() => { setShowForm(false); setEditingPolicy(null); }}>
                {tr('pages.policies.cancel')}
              </button>
              <button className="ent-btn primary" type="submit" form="policy-form">
                <Icon name="checkCircle" />
                {editingPolicy ? tr('pages.policies.saveChanges') : tr('pages.policies.createPolicy')}
              </button>
            </>
          }
        >
          <form id="policy-form" onSubmit={handleSubmit}>
            {error && (
              <div className="notice error">
                <Icon name="warning" />
                {error}
              </div>
            )}

            <div className="ent-field">
              <label htmlFor="policy-name">{tr('pages.policies.policyName')}</label>
              <input
                id="policy-name"
                className="ent-input"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={tr('pages.policies.eGDevelopersCanQueryDatabase')}
                required
              />
            </div>

            <div className="ent-field">
              <label htmlFor="policy-description">{tr('pages.policies.description')}</label>
              <input
                id="policy-description"
                className="ent-input"
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={tr('pages.policies.optionalDescription')}
              />
            </div>

            <div className="ent-field">
              <label htmlFor="policy-tags">{tr('pages.policies.tags')}</label>
              <div className="tag-input">
                {formData.tags.map((t) => (
                  <span key={t} className="tag-chip removable">
                    {t}
                    <button type="button" onClick={() => removeTag(t)} aria-label={tr('pages.policies.removeTag', { tag: t })}>
                      <Icon name="close" />
                    </button>
                  </span>
                ))}
                <input
                  id="policy-tags"
                  className="tag-input-field"
                  type="text"
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addTag(tagDraft);
                    } else if (e.key === 'Backspace' && !tagDraft && formData.tags.length) {
                      removeTag(formData.tags[formData.tags.length - 1]);
                    }
                  }}
                  onBlur={() => tagDraft && addTag(tagDraft)}
                  placeholder={formData.tags.length ? tr('pages.policies.addAnother') : tr('pages.policies.eGFinancePiiSafeProd')}
                />
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-3)' }}>
                {tr('pages.policies.pressEnterOrCommaToAdd')}
              </div>
            </div>

            <div className="ent-field">
              <label htmlFor="policy-effect">{tr('pages.policies.effect2')}</label>
              <Select
                id="policy-effect"
                value={formData.effect}
                onValueChange={(v) => setFormData({ ...formData, effect: v as 'allow' | 'deny' })}
              >
                <SelectItem value="allow">{tr('pages.policies.allowGrantAccess')}</SelectItem>
                <SelectItem value="deny">{tr('pages.policies.denyBlockAccess')}</SelectItem>
              </Select>
            </div>

            <div className="ent-field">
              <label>{tr('pages.policies.conditions')}</label>
              <div className="mode-toggle" role="group" aria-label={tr('pages.policies.conditionMode')}>
                <button
                  type="button"
                  className={mode === 'form' ? 'active' : ''}
                  onClick={() => setMode('form')}
                >
                  <Icon name="shield" /> {tr('pages.policies.formBuilder')}
                </button>
                <button
                  type="button"
                  className={mode === 'expr' ? 'active' : ''}
                  onClick={() => setMode('expr')}
                >
                  <Icon name="chip" /> {tr('pages.policies.expression')}
                </button>
                {parserEngine?.engine === 'llm' && (
                  <button
                    type="button"
                    className={mode === 'command' ? 'active' : ''}
                    onClick={() => setMode('command')}
                  >
                    <Icon name="bolt" /> {tr('pages.policies.command')}
                  </button>
                )}
              </div>
            </div>

            {mode === 'form' ? (
            <>
            <div className="ent-field">
              <label>{tr('pages.policies.roles2')}</label>
              {formData.conditions.roles.map((role, index) => (
                <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    className="ent-input"
                    type="text"
                    value={role}
                    onChange={(e) => {
                      const roles = [...formData.conditions.roles];
                      roles[index] = e.target.value;
                      setConditions({ roles });
                    }}
                    placeholder={tr('pages.policies.eGDeveloperAdminViewer')}
                  />
                  <button type="button" className="icon-btn danger" onClick={() => removeRole(index)} title={tr('pages.policies.removeRole')}>
                    <Icon name="close" />
                  </button>
                </div>
              ))}
              <div style={{ marginBottom: 12, fontSize: 11, color: 'var(--text-3)' }}>
                {tr('pages.policies.examples')} <code style={{ background: 'var(--bg-2)', padding: '2px 5px', borderRadius: '3px', marginRight: '6px' }}>developer</code>
                <code style={{ background: 'var(--bg-2)', padding: '2px 5px', borderRadius: '3px', marginRight: '6px' }}>admin</code>
                <code style={{ background: 'var(--bg-2)', padding: '2px 5px', borderRadius: '3px' }}>viewer</code>
              </div>
              <button type="button" className="ent-btn sm" onClick={addRole}>
                <Icon name="plus" />
                {tr('pages.policies.addRole')}
              </button>
            </div>

            <div className="ent-field">
              <label htmlFor="policy-tools-server">{tr('pages.policies.tools2')}</label>
              <div className="tool-picker">
                <Select id="policy-tools-server" value={selectedServer} onValueChange={handleServerSelect} placeholder={tr('pages.policies.chooseAnMCPServer')}>
                  {mcpServices.map((service) => (
                    <SelectItem key={service.name} value={service.name}>
                      {service.name}
                    </SelectItem>
                  ))}
                </Select>

                {selectedServer && availableTools.length > 0 && (
                  <div className="tool-grid">
                    {availableTools.map((tool) => {
                      const toolName = `${selectedServer}.${tool}`;
                      const added = formData.conditions.tools.includes(toolName);
                      return (
                        <button
                          key={tool}
                          type="button"
                          className={`tool-chip ${added ? 'added' : ''}`}
                          disabled={added}
                          onClick={() => !added && setConditions({ tools: [...formData.conditions.tools, toolName] })}
                        >
                          {added && <Icon name="check" />}
                          {tool === '*' ? tr('pages.policies.allTools') : tool}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {formData.conditions.tools.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{tr('pages.policies.selected')}</span>
                  <div className="token-row" style={{ marginTop: 6 }}>
                    {formData.conditions.tools.map((tool, index) => (
                      <span key={index} className="code-token selected-token">
                        {tool}
                        <button type="button" onClick={() => removeTool(index)} aria-label={tr('pages.policies.removeTool')}>
                          <Icon name="close" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            </>
            ) : mode === 'expr' ? (
            <div className="ent-field">
              <label htmlFor="policy-expr">{tr('pages.policies.expressionCEL')}</label>
              <CelEditor value={expr} onChange={setExpr} vocab={vocab} />
              <div className="expr-status">
                {validating ? (
                  <span className="muted-text">{tr('pages.policies.checking')}</span>
                ) : exprCheck && !exprCheck.valid ? (
                  <span className="expr-invalid">
                    <Icon name="warning" /> {exprCheck.errors[0]?.split('\n')[0] || tr('pages.policies.invalidExpression')}
                  </span>
                ) : exprCheck && exprCheck.valid ? (
                  <span className="expr-valid">
                    <Icon name="check" /> {tr('pages.policies.valid')}{exprCheck.warnings.length ? ` — ${exprCheck.warnings[0]}` : ''}
                  </span>
                ) : (
                  <span className="muted-text">{tr('pages.policies.typeAnExpressionPressCtrlSpace')}</span>
                )}
              </div>
              <div className="expr-hint">
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{tr('pages.policies.variables')}</span>
                <div className="token-row" style={{ marginTop: 6 }}>
                  {vocab.variables.map((v) => (
                    <CodeToken key={v.name} muted>
                      {v.name}
                    </CodeToken>
                  ))}
                </div>
                <p className="muted-text" style={{ marginTop: 8, fontSize: 11 }}>
                  {tr('pages.policies.functionsStartsWithEndsWithContainsCidrIp')}
                  <code> in</code>, <code>&amp;&amp;</code>, <code>||</code>, <code>!</code>.
                </p>
              </div>
            </div>
            ) : (
            <div className="ent-field">
              <label htmlFor="policy-command">{tr('pages.policies.describeThePolicyInPlainLanguage')}</label>
              <div className="cmd-composer">
                <textarea
                  id="policy-command"
                  className="ent-input cmd-input"
                  rows={2}
                  value={commandText}
                  onChange={(e) => setCommandText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      runParseCommand(commandText);
                    }
                  }}
                  placeholder={tr('pages.policies.eGAllowDevelopersAndViewers')}
                />
                <button
                  type="button"
                  className="ent-btn primary cmd-send"
                  disabled={parsing || !commandText.trim()}
                  onClick={() => runParseCommand(commandText)}
                >
                  {parsing ? tr('pages.policies.parsing') : (<><Icon name="bolt" /> {tr('pages.policies.parse')}</>)}
                </button>
              </div>

              <div className="cmd-examples">
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{tr('pages.policies.try')}</span>
                {COMMAND_EXAMPLES.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    className="cmd-chip"
                    onClick={() => {
                      setCommandText(ex);
                      runParseCommand(ex);
                    }}
                  >
                    {ex}
                  </button>
                ))}
              </div>

              {parseResult && (
                <div className="cmd-preview">
                  <div className="cmd-preview-head">
                    <span className={`badge badge-${parseResult.effect}`}>{parseResult.effect}</span>
                    <span className="cmd-source">
                      {parseResult.source === 'llm'
                        ? `parsed by local LLM${parserEngine?.model ? ` (${parserEngine.model})` : ''}`
                        : tr('pages.policies.parsedByRuleBasedEngine')}
                    </span>
                  </div>
                  <div className="cmd-preview-row">
                    <span className="cmd-preview-label">{tr('pages.policies.roles')}</span>
                    <span className="token-row">
                      {parseResult.roles.length ? (
                        parseResult.roles.map((r, i) => <CodeToken key={i} muted>{r}</CodeToken>)
                      ) : (
                        <span style={{ color: 'var(--text-3)' }}>—</span>
                      )}
                    </span>
                  </div>
                  <div className="cmd-preview-row">
                    <span className="cmd-preview-label">{tr('pages.policies.tools')}</span>
                    <span className="token-row">
                      {parseResult.tools.length ? (
                        parseResult.tools.map((t, i) => <CodeToken key={i}>{t}</CodeToken>)
                      ) : (
                        <span style={{ color: 'var(--text-3)' }}>—</span>
                      )}
                    </span>
                  </div>
                  {parseResult.warnings.length > 0 && (
                    <div className="cmd-warnings">
                      {parseResult.warnings.map((w, i) => (
                        <div key={i} className="expr-invalid"><Icon name="warning" /> {w}</div>
                      ))}
                    </div>
                  )}
                  <p className="muted-text" style={{ marginTop: 8, fontSize: 11 }}>
                    {tr('pages.policies.reviewAboveEditTheNameIf')}
                  </p>
                </div>
              )}
            </div>
            )}
          </form>
        </Drawer>
      )}

      {/* Delete Policy Confirmation Modal */}
      {showDeleteConfirm && (
        <Modal
          title={tr('pages.policies.deletePolicy')}
          icon="warning"
          onClose={() => !deleting && setShowDeleteConfirm(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setShowDeleteConfirm(null)} disabled={deleting === showDeleteConfirm}>
                {tr('pages.policies.cancel')}
              </button>
              <button
                className="ent-btn danger"
                onClick={confirmDelete}
                disabled={deleting === showDeleteConfirm}
              >
                <Icon name="trash" />
                {deleting === showDeleteConfirm ? tr('pages.policies.deleting') : tr('pages.policies.delete')}
              </button>
            </>
          }
        >
          <p className="muted-text">{tr('pages.policies.areYouSureYouWantTo')}</p>
        </Modal>
      )}
    </div>
  );
};

export default Policies;
